import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { ROLE_BADGE } from "@/lib/perms";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, UserCog, Trash2, UserCheck, ShieldCheck, KeyRound } from "lucide-react";

const ACTION_META = {
  "deal:assign_pic": { icon: UserCheck, tone: "text-sky-600" },
  "progress:delete": { icon: Trash2, tone: "text-rose-600" },
  "progress:update": { icon: ScrollText, tone: "text-amber-600" },
  "client:delete": { icon: Trash2, tone: "text-rose-600" },
  "activity:delete": { icon: Trash2, tone: "text-rose-600" },
  "deal:delete": { icon: Trash2, tone: "text-rose-600" },
  "stage:approved": { icon: ShieldCheck, tone: "text-emerald-600" },
  "stage:rejected": { icon: ShieldCheck, tone: "text-rose-600" },
  "user:create": { icon: UserCog, tone: "text-emerald-600" },
  "user:role_change": { icon: UserCog, tone: "text-orange-600" },
  "user:delete": { icon: UserCog, tone: "text-rose-600" },
  "user:password_reset": { icon: KeyRound, tone: "text-slate-500" },
  "auth:password_change": { icon: KeyRound, tone: "text-slate-500" },
};

const detailText = (log) => {
  const d = log.detail || {};
  const parts = [];
  if (d.pic_name) parts.push(`PIC → ${d.pic_name}`);
  if (d.deal_title) parts.push(d.deal_title);
  if (d.summary) parts.push(`"${d.summary}"`);
  if (d.author_name) parts.push(`(${d.author_name})`);
  if (d.from && d.to) parts.push(`${d.from} → ${d.to}`);
  if (d.to && !d.from) parts.push(`→ ${d.to}`);
  if (d.role) parts.push(d.role);
  if (d.name) parts.push(d.name);
  if (d.type) parts.push(d.type);
  return parts.join(" · ");
};

const AuditLog = () => {
  const { t } = useLang();
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  const load = useCallback(async () => {
    const res = await api.get("/audit-log");
    setLogs(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const actions = useMemo(() => Array.from(new Set(logs.map((l) => l.action))).sort(), [logs]);
  const filtered = useMemo(
    () => logs.filter((l) =>
      (actionFilter === "all" || l.action === actionFilter) &&
      (l.actor_email + " " + l.action + " " + l.target + " " + JSON.stringify(l.detail || {}))
        .toLowerCase().includes(q.toLowerCase())
    ),
    [logs, q, actionFilter]
  );

  return (
    <div className="space-y-6" data-testid="audit-log-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("audit_log")}</h2>
          <p className="text-sm text-slate-500 mt-1">
          {filtered.length} / {logs.length} {t("audit_entries")}
          {logs.length >= 200 && <span className="text-slate-400"> · {t("latest_200")}</span>}
        </p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder={t("search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="audit-search-input"
            className="w-56 bg-white h-9"
          />
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="audit-action-filter" className="w-[200px] h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a}>{t(`act_${a}`) === `act_${a}` ? a : t(`act_${a}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">
          <ScrollText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">{t("no_audit_log")}</p>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm divide-y divide-slate-100" data-testid="audit-log-list">
          {filtered.map((l) => {
            const meta = ACTION_META[l.action] || { icon: ScrollText, tone: "text-slate-500" };
            const Icon = meta.icon;
            const label = t(`act_${l.action}`) === `act_${l.action}` ? l.action : t(`act_${l.action}`);
            return (
              <div key={l.id} className="p-4 flex items-start gap-3" data-testid={`audit-row-${l.id}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.tone}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900">
                    <b>{l.actor_email}</b>{" "}
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border align-middle ${ROLE_BADGE[l.actor_role] || ROLE_BADGE.viewer}`}>
                      {t(l.actor_role)}
                    </span>{" "}
                    <span className="text-slate-600">{label}</span>{" "}
                    <span className="font-medium">{l.target}</span>
                  </div>
                  {detailText(l) && <div className="text-xs text-slate-500 mt-0.5 truncate">{detailText(l)}</div>}
                </div>
                <div className="text-[11px] text-slate-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
};

export default AuditLog;
